/**
 * Alert rules API client.
 *
 * Mirrors `backend/src/hub/routes/alerts.py`. Rules are
 * evaluated every minute by a DBOS-scheduled task on the server;
 * the UI is purely declarative — you set conditions + thresholds and
 * the evaluator decides when to fire.
 */
import { api } from "./api";


export type TargetKind = "all" | "workflow" | "direction" | "tag";
export type ConditionType = "failure_count" | "latency_p95" | "absence";
export type Severity = "warning" | "error" | "critical";
export type Direction = "inbound" | "internal" | "outbound";
/** For ``failure_count`` rules only — pick which population to count.
 *  ``execution`` = workflow run failures; ``delivery`` = outbox dead-letters. */
export type FailureSource = "execution" | "delivery";
/** Channel ids stored on a rule are UUID strings — they point at rows
 *  in ``notification_channels``. The in_app singleton lives at a
 *  well-known id (see ``IN_APP_CHANNEL_ID``). */
export type ChannelRef = string;

/** Well-known UUID of the in_app singleton (mirrors
 *  ``IN_APP_CHANNEL_ID`` in ``backend/src/hub/channels/service.py``).
 *  Default channel for new alert rules. */
export const IN_APP_CHANNEL_ID = "00000000-0000-0000-0000-00000000a001";


export interface AlertRule {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  target_kind: TargetKind;
  target_workflow_id: string | null;
  target_direction: Direction | null;
  target_tag: string | null;
  condition_type: ConditionType;
  condition_config: Record<string, unknown>;
  window_minutes: number;
  failure_source: FailureSource;
  severity: Severity;
  channels: ChannelRef[];
  cooldown_minutes: number;
  last_fired_at: string | null;
  last_observed_value: number | null;
  created_by: string;
  created_at: string;
  updated_at: string | null;
}


export interface AlertRuleCreate {
  name: string;
  description?: string | null;
  enabled?: boolean;
  target_kind: TargetKind;
  target_workflow_id?: string | null;
  target_direction?: Direction | null;
  target_tag?: string | null;
  condition_type: ConditionType;
  condition_config: Record<string, unknown>;
  window_minutes?: number;
  failure_source?: FailureSource;
  severity?: Severity;
  channels?: ChannelRef[];
  cooldown_minutes?: number;
}


export type AlertRuleUpdate = Partial<AlertRuleCreate>;


export interface AlertFiring {
  id: string;
  rule_id: string;
  fired_at: string;
  observed_value: number;
  notification_id: string | null;
}


export interface AlertTestResult {
  rule_id: string;
  observed_value: number;
  threshold: number;
  breached: boolean;
  in_cooldown: boolean;
  would_fire: boolean;
}


export const alertsApi = {
  list: (params?: { enabled?: boolean }) =>
    api.get<AlertRule[]>("/alerts", { query: params }),

  get: (id: string) => api.get<AlertRule>(`/alerts/${id}`),

  create: (body: AlertRuleCreate) =>
    api.post<AlertRule>("/alerts", body),

  update: (id: string, body: AlertRuleUpdate) =>
    api.patch<AlertRule>(`/alerts/${id}`, body),

  remove: (id: string) => api.delete<void>(`/alerts/${id}`),

  enable: (id: string) => api.post<AlertRule>(`/alerts/${id}/enable`),
  disable: (id: string) => api.post<AlertRule>(`/alerts/${id}/disable`),

  /** Dry-run — evaluates now but doesn't fire. */
  test: (id: string) => api.post<AlertTestResult>(`/alerts/${id}/test`),

  firings: (id: string, limit = 50) =>
    api.get<AlertFiring[]>(`/alerts/${id}/firings`, { query: { limit } }),
};
